import _ from 'lodash';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { fToNow, fDateTime } from 'src/utils/format-time';

import { OpenStatus, IApplicationListItem } from 'src/services/applications/applications.types';

import Label from 'src/components/label';
import EntityAvatar from 'src/components/entity-avatar';
import StarRating from 'src/components/rating/star-rating';
import { ColumnDef } from 'src/components/data-list/data-list.types';
import { OrgCandidateTypeIcon } from 'src/components/candidates/candidate-type.icon';

import { ApplicationStageLabel } from 'src/sections/jobs/applications/applications-stage-label';

// ----------------------------------------------------------------------

type Translate = (key: string, options?: any) => string;

interface BuildApplicationColumnsArgs {
  t: Translate;
  /** Opens the CANDIDATE profile overlay. No-ops for rows with no CRM record. */
  onOpenCandidate: (orgCandidateId: string) => void;
  /** Writes a new star rating for the application. */
  onRatingUpdate: (applicationId: string, rating: number) => void;
}

const dash = <Typography variant="body2" color="textSecondary">--</Typography>;

/**
 * Application list column registry.
 *
 * Mirrors buildCandidateColumns: a factory so interactive columns close over their callbacks, with
 * `key` doubling as the server sort field and per-column search field.
 */
export function buildApplicationColumns({ t, onOpenCandidate, onRatingUpdate }: BuildApplicationColumnsArgs): ColumnDef<IApplicationListItem>[] {
  return [
    {
      key: 'name',
      label: t('applications.table.name', { defaultValue: 'Applicant' }),
      fixed: true,
      sortable: true,
      searchable: true,
      minWidth: 240,
      render: (row) => {
        const name = _.capitalize(`${row.first_name ?? ''} ${row.last_name ?? ''}`.trim());
        const hasCandidate = Boolean(row.org_candidate_id);

        return (
          <Stack direction="row" alignItems="flex-start" gap={1}>
            <EntityAvatar src={row.photo_url} name={name} size={28} sx={{ mt: 0.25 }} />

            {/* Linked/unlinked indicator — only meaningful when the org holds a CRM record. */}
            {row.org_candidate_type && (
              <Box sx={{ mt: 0.5 }}>
                <OrgCandidateTypeIcon type={row.org_candidate_type} />
              </Box>
            )}

            <Stack direction="column">
              <Stack direction="row" alignItems="center" gap={0.5}>
                {hasCandidate ? (
                  <Link
                    component="button"
                    type="button"
                    onClick={() => onOpenCandidate(row.org_candidate_id as string)}
                    color="inherit"
                    underline="hover"
                    noWrap
                    sx={{ textTransform: 'capitalize', textAlign: 'left' }}
                  >
                    <Typography variant="subtitle2">{name || '--'}</Typography>
                  </Link>
                ) : (
                  // No CRM record to open — render plain text rather than a button that does nothing.
                  <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>{name || '--'}</Typography>
                )}

                {row.open_status === OpenStatus.NEW && (
                  <Label color="primary">{t('common.new')}</Label>
                )}
              </Stack>
            </Stack>
          </Stack>
        );
      },
    },
    {
      key: 'job',
      label: t('applications.table.job', { defaultValue: 'Job applied for' }),
      defaultVisible: true,
      sortable: true,
      searchable: true,
      minWidth: 200,
      render: (row) => {
        if (!row.job_title) return dash;

        // A real href (not a button) so the job opens on middle-click / "open in new tab" too. The
        // applicant name deliberately stays a button — that opens the overlay without navigating,
        // whereas the job is a hard navigation away from the list.
        if (!row.job_id) return <Typography variant="body2">{row.job_title}</Typography>;

        return (
          <Link
            component={RouterLink}
            href={paths.dashboard.jobs.detail(row.job_id)}
            variant="body2"
            underline="hover"
            color="inherit"
          >
            {row.job_title}
          </Link>
        );
      },
    },
    {
      key: 'phone',
      label: t('applications.table.phone', { defaultValue: 'Phone number' }),
      defaultVisible: true,
      minWidth: 150,
      // Not sortable: the server returns this only to the managing org, so sorting would order by a
      // value half the rows don't carry.
      render: (row) => (row.phone_number
        ? <Link variant="body2" underline="hover" color="InfoText" href={`tel:${row.phone_number}`}>{row.phone_number}</Link>
        : dash),
    },
    {
      key: 'location',
      label: t('applications.table.location', { defaultValue: 'Location' }),
      // Off by default to keep the default layout narrow; still sortable/searchable once a user
      // enables it from the column picker.
      defaultVisible: false,
      sortable: true,
      searchable: true,
      render: (row) => (row.location
        ? <Typography variant="body2" color="text.secondary">{row.location}</Typography>
        : dash),
    },
    {
      key: 'created_at',
      label: t('applications.table.created_at', { defaultValue: 'Date received' }),
      defaultVisible: true,
      sortable: true,
      render: (row) => (row.created_at ? (
        <Tooltip title={fToNow(row.created_at)}>
          <Typography variant="body2" color="text.secondary">{fDateTime(row.created_at)}</Typography>
        </Tooltip>
      ) : dash),
    },
    {
      key: 'source',
      label: t('applications.table.source', { defaultValue: 'Source' }),
      defaultVisible: true,
      sortable: true,
      searchable: true,
      render: (row) => {
        // The org-configurable apply source is the more specific of the two, so it wins when set.
        const value = row.apply_source
          || (row.source ? t(`enums.application_source.${row.source}`, { defaultValue: row.source }) : null);
        return value ? <Typography variant="body2" color="text.secondary">{value}</Typography> : dash;
      },
    },
    {
      key: 'stage',
      label: t('applications.table.stage', { defaultValue: 'Current stage' }),
      defaultVisible: true,
      sortable: true,
      searchable: true,
      render: (row) => (row.stage ? <ApplicationStageLabel status={row.stage} /> : dash),
    },
    {
      key: 'last_activity_at',
      label: t('applications.table.last_activity', { defaultValue: 'Last activity' }),
      defaultVisible: true,
      sortable: true,
      render: (row) => (row.last_activity_at ? (
        <Tooltip title={fToNow(row.last_activity_at)}>
          <Typography variant="body2" color="text.secondary">{fDateTime(row.last_activity_at)}</Typography>
        </Tooltip>
      ) : dash),
    },
    {
      key: 'rating',
      label: t('applications.table.rating', { defaultValue: 'Rating' }),
      // Last in the registry, so it renders at the end of the default layout.
      defaultVisible: true,
      // StarRating disables every star when onSelectExcitementRating is absent, so this callback is
      // what makes the column editable rather than a static display.
      render: (row) => (
        <StarRating rating={row.rating} onSelectExcitementRating={(rating) => onRatingUpdate(row.id, rating)} />
      ),
    },
  ];
}
